import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { UnlinkCalendarName } from 'model/calendar.model';
import { ClientType } from 'model/client.model';
import { AuthService } from 'service/auth.service';
import { CalendarService } from 'service/calendar.service';
import { ClientService } from 'service/client.service';
import { HuddleClientService } from 'service/huddle-client.service';
import zeplinColor from 'theme/Color';
import { MsTeamsRawData, validateMsTeamsData } from 'utilities/CalendarUtils';

interface CalendarState {
  rawEvents: any[];
  clients: ClientType[];
  accounts: string[];
  filteredEvents: any[];
  loadingStates: {
    huddle: boolean;
    google: boolean;
    zoom: boolean;
    msTeams: boolean;
  };
  filters: string[];
  error: string | null;
  connections: {
    isGoogleConnected: boolean;
    isZoomConnected: boolean;
    isMsConnected: boolean;
  };
  unlinkLoading: boolean;
  otherCalendars: { name: string }[];
  colors: Record<string, string>;
}

const initialState: CalendarState = {
  rawEvents: [],
  clients: [],
  accounts: [],
  filteredEvents: [],
  loadingStates: { huddle: false, google: false, zoom: false, msTeams: false },
  filters: [],
  error: null,
  connections: {
    isGoogleConnected: sessionStorage.getItem('isGoogleConnected') === 'true',
    isZoomConnected: sessionStorage.getItem('isZoomConnected') === 'true',
    isMsConnected: sessionStorage.getItem('MSToken') === 'true',
  },
  unlinkLoading: false,
  otherCalendars: [],
  colors: {
    huddle: localStorage.getItem('calendar_color_huddle') || zeplinColor.USER_PROFILE_COLOR13,
  },
};

const platformMatchers = {
  Google: (ev: any) => ev.type === 'Google' || ev.selectedCalendar?.startsWith('google:'),
  Zoom: (ev: any) => ev.type === 'Zoom' || ev.selectedCalendar?.startsWith('zoom:'),
  MSTeams: (ev: any) => ev.type === 'MSTeams' || ev.selectedCalendar?.startsWith('microsoft:'),
  Huddle: (ev: any) => ev.type?.toLowerCase() === 'huddle',
};

const applyFilters = (rawEvents: any[], activeFilters: string[]) => {
  return rawEvents.filter((event) => {
    const eventKey = event.selectedCalendar?.split(':')[1] || event.type;
    if (activeFilters.length === 0) return true;
    const isHidden = activeFilters.includes(eventKey);

    return !isHidden;
  });
};

export const fetchHuddleEvents = createAsyncThunk(
  'calendar/fetchHuddleEvents',
  async (clientId?: string) => {
    if (clientId) {
      const clientData = await HuddleClientService.getClientEvents(clientId);
      if (clientData?.events) {
        return clientData.events;
      }
    } else {
      const userData = await HuddleClientService.getUserEvents();
      if (userData?.events) {
        return userData.events;
      }
    }
  },
);

export const fetchGoogleEvents = createAsyncThunk(
  'calendar/fetchGoogle',
  async ({ month, year }: { month: number; year?: number }) => {
    const res = await CalendarService.getGoogleEvents({ month, year });
    return Object.keys(res).flatMap((user) =>
      res[user].map((val: any) => ({ ...val, selectedCalendar: `google:${user}`, type: 'Google' })),
    );
  },
);

export const fetchZoomEvents = createAsyncThunk(
  'calendar/fetchZoom',
  async ({ month, year }: { month: number; year?: number }) => {
    const res = await CalendarService.getZoomEvents({ month, year });
    return Object.keys(res).flatMap((user) =>
      res[user].map((val: any) => ({ ...val, selectedCalendar: `zoom:${user}`, type: 'Zoom' })),
    );
  },
);

export const fetchMsTeamsEvents = createAsyncThunk(
  'calendar/fetchMsTeams',
  async (res?: undefined | MsTeamsRawData) => {
    let result = res ? validateMsTeamsData(res) : null;

    if (!result) {
      const email = sessionStorage.getItem('current-user') || '';
      result = await AuthService.ms_team_events('', email, true);
    }

    if (result) {
      return Object.keys(result).flatMap((user) =>
        result![user].map((val: any) => ({
          ...val,
          selectedCalendar: `microsoft:${user}`,
          type: 'MSTeams',
        })),
      );
    }
    return [];
  },
);

export const fetchAllClients = createAsyncThunk('calendar/fetchAllClients', async () => {
  const [sfRes, huddleRes] = await Promise.all([
    ClientService.getAllClient(),
    HuddleClientService.getAllClient(),
  ]);

  const salesforceClients = sfRes?.clients.map((val: any) => ({ ...val, Id: val.AccountId })) || [];
  const huddleClients = huddleRes?.clients.map((val: any) => ({ ...val, Id: val.AccountId })) || [];

  return [...salesforceClients, ...huddleClients].sort((a, b) => a.Name.localeCompare(b.Name));
});

export const fetchAllAccounts = createAsyncThunk('calendar/fetchAllAccounts', async () => {
  return await CalendarService.getAllAccounts();
});

export const saveCalendarEvent = createAsyncThunk(
  'calendar/saveEvent',
  async ({ type, payload, isEdit, eventId, activeClientId }: any, { dispatch }) => {
    console.log(type, '$$$%%%');
    const platform = type.split(':')[0];
    const calendarEmail = type.split(':')[1];
    let response;

    if (platform === 'huddle') {
      if (isEdit) {
        response = activeClientId
          ? await HuddleClientService.editClientEvent(activeClientId, eventId, payload)
          : await HuddleClientService.editUserEvent(eventId, payload);
      } else {
        response = activeClientId
          ? await HuddleClientService.addClientEvent(payload, activeClientId)
          : await HuddleClientService.addUserEvent(payload);
      }
    } else if (platform === 'microsoft') {
      const msPayload = {
        subject: payload.meetingName,
        start: payload.start,
        end: payload.end,
        timezone: 'UTC',
        ...(payload.attendees && { attendees: payload.attendees }),
        recurrence: payload.recurrence,
      };
      response = isEdit
        ? await CalendarService.editMSEvents(eventId, calendarEmail, msPayload)
        : await CalendarService.createMSEvents(calendarEmail, msPayload);
    } else {
      const services: any = {
        google: isEdit ? CalendarService.editGoogleEvents : CalendarService.createGoogleEvents,
        zoom: isEdit ? CalendarService.editZoomEvents : CalendarService.createZoomEvents,
      };
      response = isEdit
        ? await services[platform](eventId, calendarEmail, payload)
        : await services[platform](calendarEmail, payload);
    }

    if (platform === 'huddle') {
      dispatch(fetchHuddleEvents(activeClientId));
    } else if (platform === 'google') {
      dispatch(fetchGoogleEvents({ month: dayjs().month() + 1 }));
    } else if (platform === 'zoom') {
      dispatch(fetchZoomEvents({ month: dayjs().month() + 1 }));
    } else if (platform === 'microsoft') {
      dispatch(fetchMsTeamsEvents());
    }
    return response;
  },
);

export const deleteCalendarEvent = createAsyncThunk(
  'calendar/deleteEvent',
  async (
    { type, eventId, activeClientId }: { type: string; eventId: string; activeClientId?: string },
    { dispatch },
  ) => {
    const platform = type.split(':')[0];
    const calendarEmail = type.split(':')[1];

    if (platform === 'huddle') {
      activeClientId
        ? await HuddleClientService.deleteClientEvent(activeClientId, eventId)
        : await HuddleClientService.deleteUserEvent(eventId);
    } else if (platform === 'google') {
      await CalendarService.deleteGoogleEvents(calendarEmail, eventId);
    } else if (platform === 'zoom') {
      await CalendarService.deleteZoomEvents(calendarEmail, eventId);
    } else if (platform === 'microsoft') {
      await CalendarService.deleteMSEvents(calendarEmail, eventId);
    }

    if (platform === 'huddle') {
      dispatch(fetchHuddleEvents(activeClientId));
    } else if (platform === 'google') {
      dispatch(fetchGoogleEvents({ month: dayjs().month() + 1 }));
    } else if (platform === 'zoom') {
      dispatch(fetchZoomEvents({ month: dayjs().month() + 1 }));
    } else if (platform === 'microsoft') {
      dispatch(fetchMsTeamsEvents());
    }

    return { eventId, platform };
  },
);

export const handleCalendarConnect = createAsyncThunk(
  'calendar/connect',
  async ({ code, provider }: { code: string; provider: string }, { dispatch }) => {
    const email = sessionStorage.getItem('current-user') || '';
    let response;

    if (provider?.toLocaleLowerCase() === 'microsoft teams') {
      response = await AuthService.ms_team_events(code, email, false);
    } else if (provider?.toLocaleLowerCase() === 'zoom') {
      response = await AuthService.zoom_auth(code, process.env.REACT_APP_REDIRECT || '');
    } else {
      response = await AuthService.google_auth(code, process.env.REACT_APP_REDIRECT || '');
    }
    return { provider, data: response };
  },
);

export const unlinkCalendar = createAsyncThunk(
  'calendar/unlink',
  async ({ provider, email }: { provider: string; email: string }) => {
    const integration =
      provider === 'google'
        ? UnlinkCalendarName.google
        : provider === 'zoom'
          ? UnlinkCalendarName.zoom
          : UnlinkCalendarName['ms-teams'];

    await CalendarService.unlinkAccount(integration, email);
    sessionStorage.setItem(
      provider === 'Microsoft Teams' ? 'MSToken' : `is${provider}Connected`,
      'false',
    );
    return { provider, email };
  },
);

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    updateConnectionStatus: (
      state,
      action: PayloadAction<Partial<CalendarState['connections']>>,
    ) => {
      state.connections = { ...state.connections, ...action.payload };
    },
    clearEvents: (state) => {
      state.rawEvents = [];
    },
    clearPlatformEvents: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      const typeMap: Record<string, string> = {
        isGoogleConnected: 'Google',
        isZoomConnected: 'Zoom',
        MSToken: 'MSTeams',
      };
      const typeToRemove = typeMap[key];
      state.rawEvents = state.rawEvents.filter((ev) => ev.type !== typeToRemove);
      state.filteredEvents = state.filteredEvents.filter((ev) => ev.type !== typeToRemove);
    },
    toggleFilter: (state, action: PayloadAction<{ tool: string; isVisible: boolean }>) => {
      const { tool, isVisible } = action.payload;
      if (isVisible) {
        state.filters = state.filters.filter((f) => f !== tool);
      } else {
        if (!state.filters.includes(tool)) {
          state.filters.push(tool);
        }
      }
      state.filteredEvents = applyFilters(state.rawEvents, state.filters);
    },
    updateCalendarColor: (state, action: PayloadAction<{ provider: string; color: string }>) => {
      const { provider, color } = action.payload;
      state.colors[provider] = color;
      localStorage.setItem(`calendar_color_${provider}`, color);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHuddleEvents.pending, (state) => {
        state.loadingStates.huddle = false;
      })
      .addCase(fetchHuddleEvents.fulfilled, (state, action) => {
        const nonHuddleEvents = state.rawEvents.filter(
          (event) => event.type.toLowerCase() !== 'huddle',
        );
        state.rawEvents = [...nonHuddleEvents, ...action.payload];
        state.filteredEvents = applyFilters(state.rawEvents, state.filters);
        state.loadingStates.huddle = false;
      })
      .addCase(fetchHuddleEvents.rejected, (state) => {
        state.loadingStates.huddle = false;
      })
      .addCase(fetchGoogleEvents.pending, (state) => {
        state.loadingStates.google = false;
      })
      .addCase(fetchGoogleEvents.fulfilled, (state, action) => {
        const otherEvents = state.rawEvents.filter((ev) => !platformMatchers.Google(ev));
        state.rawEvents = [...otherEvents, ...action.payload];
        state.filteredEvents = applyFilters(state.rawEvents, state.filters);
        state.loadingStates.google = false;
      })
      .addCase(fetchGoogleEvents.rejected, (state) => {
        state.loadingStates.google = false;
      })
      .addCase(fetchZoomEvents.pending, (state) => {
        state.loadingStates.zoom = true;
      })
      .addCase(fetchZoomEvents.fulfilled, (state, action) => {
        const otherEvents = state.rawEvents.filter((ev) => !platformMatchers.Zoom(ev));
        state.rawEvents = [...otherEvents, ...action.payload];
        state.filteredEvents = applyFilters(state.rawEvents, state.filters);
        state.loadingStates.zoom = false;
      })
      .addCase(fetchZoomEvents.rejected, (state) => {
        state.loadingStates.zoom = false;
      })
      .addCase(fetchMsTeamsEvents.pending, (state) => {
        state.loadingStates.msTeams = true;
      })
      .addCase(fetchMsTeamsEvents.fulfilled, (state, action) => {
        state.loadingStates.msTeams = false;
        const others = state.rawEvents.filter((ev) => !platformMatchers.MSTeams(ev));
        state.rawEvents = [...others, ...action.payload];
        state.filteredEvents = applyFilters(state.rawEvents, state.filters);
        state.loadingStates.msTeams = false;
      })
      .addCase(fetchMsTeamsEvents.rejected, (state) => {
        state.loadingStates.msTeams = false;
      })
      .addCase(fetchAllClients.fulfilled, (state, action) => {
        state.clients = action.payload;
      })
      .addCase(fetchAllAccounts.fulfilled, (state, action) => {
        state.accounts = action.payload;
        if (action?.payload?.googleAccounts?.length > 0) {
          state.connections.isGoogleConnected = true;
        }
        if (action?.payload?.zoomAccounts?.length > 0) {
          state.connections.isZoomConnected = true;
        }
        if (action?.payload?.msTeamsAccounts?.length > 0) {
          state.connections.isMsConnected = true;
        }
        const categories = [
          { list: action.payload?.googleAccounts, type: 'google' },
          { list: action.payload?.zoomAccounts, type: 'zoom' },
          { list: action.payload?.msTeamsAccounts, type: 'microsoft' },
        ];

        categories.forEach(({ list, type }) => {
          list?.forEach((acc: any) => {
            const compositeId = `${type}:${acc.email}`;
            const savedColor = localStorage.getItem(`calendar_color_${compositeId}`);

            if (savedColor) {
              state.colors[compositeId] = acc?.color;
            } else if (!state.colors[compositeId]) {
              state.colors[compositeId] = state.colors[type] || zeplinColor.Brand1;
            }
          });
        });
      })
      .addCase(handleCalendarConnect.fulfilled, (state, action) => {
        const { provider } = action.payload;

        if (provider === 'Google Meet') state.connections.isGoogleConnected = true;
        if (provider === 'Zoom') state.connections.isZoomConnected = true;
        if (provider === 'Microsoft Teams') state.connections.isMsConnected = true;

        const storageKey =
          provider === 'Microsoft Teams'
            ? 'MSToken'
            : provider === 'Zoom'
              ? 'isZoomConnected'
              : 'isGoogleConnected';
        sessionStorage.setItem(storageKey, 'true');
      })
      .addCase(unlinkCalendar.pending, (state, action) => {
        state.unlinkLoading = true;
      })
      .addCase(unlinkCalendar.fulfilled, (state, action) => {
        const { provider, email } = action.payload;

        if (provider === 'google') state.connections.isGoogleConnected = false;
        if (provider === 'zoom') state.connections.isZoomConnected = false;
        if (provider === 'microsoft') state.connections.isMsConnected = false;

        const typeMap: Record<string, string> = {
          google: 'Google',
          zoom: 'Zoom',
          microsoft: 'MSTeams',
        };
        const typeToRemove = typeMap[provider];

        state.rawEvents = state.rawEvents.filter((ev) => {
          const [_, emailSelected] = (ev?.selectedCalendar || '').split(':');

          return !(ev?.type === typeToRemove && email === emailSelected);
        });
        state.filteredEvents = state.filteredEvents.filter((ev) => {
          const [_, emailSelected] = (ev?.selectedCalendar || '').split(':');
          return !(ev?.type === typeToRemove && email === emailSelected);
        });

        state.unlinkLoading = false;
      })
      .addCase(deleteCalendarEvent.fulfilled, (state, action) => {
        const { eventId } = action.payload;
        state.rawEvents = state.rawEvents.filter((ev) => ev.id !== eventId);
        state.filteredEvents = applyFilters(state.rawEvents, state.filters);
      });
  },
});

export const {
  updateConnectionStatus,
  clearEvents,
  toggleFilter,
  clearPlatformEvents,
  updateCalendarColor,
} = calendarSlice.actions;
export default calendarSlice.reducer;
